import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
/**
 * FE#192 — Build DBWaContactsCache entities for the @newsletter (channel) chats
 * returned by waQueryContactsAndChatsTool, so channels become selectable
 * recipients in the schedule-dialog picker.
 *
 * Why this is needed: the recipient picker (ContactPickerField →
 * UnifiedContactSelector) is backed by the WhatsApp contacts cache
 * (DBWaContactsCacheReducer) via selectFilteredContactsMinimal. Contacts and
 * groups reach that cache through the `contacts` array (getContacts reads
 * Store.Contact, which has NO newsletters). Newsletters live in a separate WA
 * store (WAWebNewsletterMetadataCollection) and surface only inside the `chats`
 * array — where the lean chat shape (createLeanChat, post #63/#64) carries its
 * serialized id on `chatId`. The cache loader's chat→entity mapping only read
 * `chat._id / chat.id._serialized / chat._serialized / chat.id`, never
 * `chat.chatId`, so newsletter chats resolved to _id=undefined and were
 * dropped — the picker's "Channels" type-filter therefore filtered an empty
 * set and a 2nd channel could never be added to a scheduled post.
 *
 * This projects each @newsletter chat into the cache entity shape the picker
 * source understands: a top-level `_id` (the entity-adapter key — see
 * DBServiceReducers selectId) plus `id._serialized` (read by the minimal mapper
 * and getContactSerializedId).
 */

export interface NewsletterCacheEntity {
    _id: string;
    id: { _serialized: string };
    name?: string;
    pushname?: string;
    isGroup: false;
    _isChat: true;
}

/**
 * Resolve a chat's serialized id across the canonical lean shape (`chatId`,
 * post #63/#64) and the legacy `id._serialized` / `_serialized` / `_id` forms.
 * Exported (FE#434) so the cache loader's chat→entity mapping uses the SAME
 * chatId-first resolution — otherwise lean group/DM chats resolve to an empty
 * id and get dropped (the groups-missing bug), exactly as newsletters did.
 */
export const chatSerializedId = (chat: any): string =>
    chat?.chatId ?? chat?.id?._serialized ?? chat?._serialized ?? chat?._id ?? '';

/** Permissive view of a chat row as returned by waQueryContactsAndChatsTool —
 *  the canonical LEAN shape (`chatId`, post #63/#64) plus the legacy aliases a
 *  few paths still emit. Index signature covers the spread-through fields the
 *  cache entity carries verbatim. */
export interface ChatRowLike {
    chatId?: string;
    _id?: string;
    _serialized?: string;
    id?: { _serialized?: string };
    name?: string | null;
    formattedTitle?: string | null;
    pushname?: string | null;
    [k: string]: unknown;
}

/** One DBWaContactsCache entity projected from a chat row. */
export interface ChatContactCacheEntity extends ChatRowLike {
    _id: string;
    name?: string | null;
    pushname?: string | null;
    _isChat: true;
    _originalIndex: number;
}

/**
 * FE#434 — project the `chats` array (groups + DMs + newsletters) into
 * DBWaContactsCache entities for the extension recipient picker. The entity key
 * `_id` MUST resolve chatId-first (`chatSerializedId`): post wire-contract 3.8
 * (ext 5.9.34+) the rows are LEAN (`createLeanChat` — id only on `chatId`), and
 * the old `chat._id || chat.id?._serialized || chat._serialized || chat.id`
 * derivation resolved to `undefined`, so the thunk's merge (`if (item._id …)`)
 * DROPPED every chat. GROUPS reach the cache ONLY via this array (getContacts is
 * @c.us persons-only), so every group vanished from the Groups tab. Extracted so
 * the cache-loader's real mapping is unit-tested (a regression here fails fast).
 */
export function chatsToContactCacheEntities(chats: ReadonlyArray<ChatRowLike>): ChatContactCacheEntity[] {
    if (!Array.isArray(chats)) return [];
    return chats.map((chat, index): ChatContactCacheEntity => ({
        ...chat,
        _id: chatSerializedId(chat) || chat._id || chat.id?._serialized || chat._serialized || (chat.id as unknown as string),
        name: chat.name || chat.formattedTitle || chat.pushname || null,
        pushname: chat.pushname || chat.name || null,
        _isChat: true,
        _originalIndex: index,
    }));
}

export function newsletterChatsToCacheEntities(chats: any[]): NewsletterCacheEntity[] {
    if (!Array.isArray(chats)) return [];

    const seen = new Set<string>();
    const entities: NewsletterCacheEntity[] = [];

    for (const chat of chats) {
        if (!chat) continue;
        const serialized = chatSerializedId(chat);
        if (chatKindOf(serialized) !== 'channel') continue;
        if (seen.has(serialized)) continue;
        seen.add(serialized);

        const name: string | undefined = chat.name || chat.formattedTitle || chat.pushname || undefined;
        entities.push({
            _id: serialized,
            id: { _serialized: serialized },
            name,
            pushname: chat.pushname || chat.name || undefined,
            isGroup: false,
            _isChat: true,
        });
    }

    return entities;
}
